// T-272 — SystemPrintAdapter: stampa un'etichetta tramite il driver di sistema
// del PC, usando `window.print()` su un iframe nascosto. Pensato per la PWA
// Chrome su desktop (es. cliente Bluecoop con la Meteor Mirò collegata via USB):
// il browser non può aprire la USB direttamente in modo affidabile su Windows
// (WebUSB richiede di sostituire il driver con WinUSB), ma può stampare verso
// qualunque stampante installata nel sistema. L'etichetta viene renderizzata
// col rasterizzatore reale (renderLabelCanvas, 203 DPI, barcode/QR veri) → PNG
// → documento dimensionato in mm 1:1 → print. Con Chrome avviato in
// `--kiosk-printing` e la stampante predefinita, esce a 1 tap senza dialogo.
//
// Disponibile SOLO su web (su nativo Android/iOS si usano BLE/BT Classic).

import { Capacitor } from '@capacitor/core';
import type {
  PrinterAdapter,
  PrinterDevice,
  PrinterSession,
  PrinterStatus,
  LabelTemplate,
} from '@trackeat/shared';
import { renderLabelCanvas } from './labelRaster.js';
import { buildSystemPrintDocument } from './system-print-doc.js';

// Identità della stampante "di sistema". Address convenzionale: la stampante
// fisica reale la sceglie Windows/Chrome (il browser non lascia a JS scegliere
// il device), qui rappresentiamo solo il canale "stampa di sistema".
export const SYSTEM_PRINTER_ID = 'system-default';
export const SYSTEM_PRINTER_NAME = 'Stampante di sistema (Windows)';
export const SYSTEM_PRINTER_ADDRESS = 'system://default';

const PRINT_IMAGE_TIMEOUT_MS = 4_000;
// Fallback: rimuovi l'iframe solo se onafterprint non scatta. Deve essere lungo
// perché su alcune stampanti/driver lo spooler legge il DOM dell'iframe DOPO
// che print() è ritornato — rimuoverlo troppo presto = non esce nulla.
const IFRAME_CLEANUP_FALLBACK_MS = 60_000;

/**
 * Scrive l'HTML in un iframe fuori schermo e chiama `print()`. Risolve quando
 * il print è stato avviato. In `--kiosk-printing` è silenzioso; altrimenti
 * compare il dialogo di Chrome.
 *
 * IMPORTANTE: l'iframe NON deve essere `visibility:hidden`/`display:none` né
 * 0×0, altrimenti il browser stampa una pagina VUOTA (la `visibility` è onorata
 * anche in stampa). Lo teniamo fuori schermo (`left:-10000px`) con dimensioni
 * reali e `opacity:0`. Usiamo `document.write` (più affidabile di `srcdoc` per
 * la stampa) e attendiamo il caricamento dell'immagine prima di stampare.
 */
export function printHtmlViaIframe(html: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    Object.assign(iframe.style, {
      position: 'fixed',
      left: '-10000px',
      top: '0',
      width: '210mm',
      height: '297mm',
      border: '0',
      opacity: '0',
      pointerEvents: 'none',
    } as Partial<CSSStyleDeclaration>);

    let settled = false;
    let removed = false;
    const removeIframe = (): void => {
      if (removed) return;
      removed = true;
      try { iframe.remove(); } catch { /* già rimosso */ }
    };

    const triggerPrint = (): void => {
      if (settled) return;
      const win = iframe.contentWindow;
      if (!win) {
        settled = true;
        removeIframe();
        reject(new Error('Stampa di sistema non disponibile (iframe).'));
        return;
      }
      settled = true;
      try {
        // Rimuovi l'iframe SOLO a stampa completata (onafterprint): rimuoverlo
        // subito dopo print() fa uscire pagine vuote / niente su molti driver.
        win.onafterprint = removeIframe;
        window.setTimeout(removeIframe, IFRAME_CLEANUP_FALLBACK_MS);
        win.focus();
        win.print();
        resolve();
      } catch (err) {
        removeIframe();
        reject(err instanceof Error ? err : new Error(String(err)));
      }
    };

    try {
      document.body.appendChild(iframe);
      const doc = iframe.contentWindow?.document;
      if (!doc) {
        removeIframe();
        reject(new Error('Stampa di sistema non disponibile (documento iframe).'));
        return;
      }
      // document.write è più affidabile di srcdoc per il print pipeline.
      doc.open();
      doc.write(html);
      doc.close();

      const imgs = Array.from(doc.images);
      if (imgs.length === 0) { triggerPrint(); return; }
      let remaining = imgs.length;
      const oneDone = (): void => {
        remaining -= 1;
        if (remaining <= 0) triggerPrint();
      };
      for (const img of imgs) {
        if (img.complete) { oneDone(); continue; }
        img.addEventListener('load', oneDone, { once: true });
        img.addEventListener('error', oneDone, { once: true });
      }
      // Rete di sicurezza: stampa comunque se un'immagine non emette load.
      window.setTimeout(triggerPrint, PRINT_IMAGE_TIMEOUT_MS);
    } catch (err) {
      removeIframe();
      reject(err instanceof Error ? err : new Error(String(err)));
    }
  });
}

export class SystemPrintAdapter implements PrinterAdapter {
  readonly driver = 'system' as const;

  async discover(): Promise<PrinterDevice[]> {
    // Solo su web: su nativo si usano i transport BLE/BT Classic.
    if (Capacitor.isNativePlatform()) return [];
    return [{
      id: SYSTEM_PRINTER_ID,
      name: SYSTEM_PRINTER_NAME,
      driver: 'system',
      connection: 'usb',
      address: SYSTEM_PRINTER_ADDRESS,
      model_hint: null,
    }];
  }

  async connect(device: PrinterDevice): Promise<PrinterSession> {
    // Nessuna connessione fisica: la stampa passa dal sistema operativo.
    return { deviceId: device.address, driver: this.driver, connection: 'usb' };
  }

  async disconnect(_session: PrinterSession): Promise<void> {
    // no-op
  }

  async printLabel(
    _session: PrinterSession,
    template: LabelTemplate,
    data: Record<string, unknown>,
    copies: number = 1,
  ): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      throw new Error('La stampa di sistema è disponibile solo nella versione web (PWA).');
    }
    // Stesso canvas usato per la stampa Bluetooth (203 DPI, barcode/QR veri,
    // accenti gestiti) → parità di resa con le etichette delle altre stampanti.
    const canvas = renderLabelCanvas(template, data);
    const png = canvas.toDataURL('image/png');
    const html = buildSystemPrintDocument({
      pngDataUrl: png,
      widthMm: template.width_mm,
      heightMm: template.height_mm,
      copies,
      title: template.name,
    });
    await printHtmlViaIframe(html);
  }

  async getStatus(_session: PrinterSession): Promise<PrinterStatus> {
    return {
      online: true,
      paper_ok: true,
      cover_closed: true,
      battery_pct: null,
      error_code: null,
    };
  }
}
